import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deletePerson, getPerson, provisionPersonSoftware } from '@/api/people';
import { listAssets } from '@/api/assets';
import { getLocations } from '@/api/locations';
import { listDepartmentSoftware } from '@/api/departmentSoftware';
import { getAudit } from '@/api/audit';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { formatDate, formatDateTime } from '@/lib/dateFormat';
import type { DepartmentSoftware, Person } from '@/types/oscar';
import { PersonOnsiteLive } from './PersonOnsiteLive';
import { PersonLeaving } from './PersonLeaving';
import { CostCentreSplit } from './CostCentreSplit';

type Tab = 'profile' | 'devices' | 'held' | 'software' | 'sims' | 'onsitelive' | 'leaving' | 'audit';
const TABS_BY_PARAM: Tab[] = ['onsitelive', 'leaving'];

export function PersonDetail() {
  const { id } = useParams();
  const personId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canMutate, isAdmin } = useAuth();
  // ?tab=onsitelive / ?tab=leaving opens a tab directly (the OnsiteLive users page links here).
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => {
    const t = searchParams.get('tab') as Tab | null;
    return t && TABS_BY_PARAM.includes(t) ? t : 'profile';
  });
  const [confirmOpen, setConfirmOpen] = useState(false);

  const query = useQuery({
    queryKey: ['person', personId],
    queryFn: ({ signal }) => getPerson(personId, signal),
  });

  const del = useMutation({
    mutationFn: () => deletePerson(personId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['people'] });
      navigate('/people');
    },
  });

  if (query.isLoading) return <p className="text-sm text-darkGrey/70">Loading…</p>;
  if (query.isError || !query.data) return <p className="text-sm text-red">Could not load this person.</p>;

  const { Person: p, Counts: counts } = query.data;

  return (
    <section>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <Link to="/people" className="text-xs text-navy hover:underline">← People</Link>
          <h1 className="font-heading text-2xl font-bold text-navy">{p.DisplayName}</h1>
          <p className="text-sm text-darkGrey/80">
            {p.JobTitle ?? '—'} · {p.AccountStatus}
            {p.Active === false && (
              <span
                title="This account is disabled in Entra (synced). Separate from the workflow status above."
                className="ml-2 inline-flex rounded-full bg-red/10 px-2 py-0.5 text-xs font-semibold text-red align-middle"
              >
                Disabled in Entra
              </span>
            )}
          </p>
        </div>
        {canMutate && (
          <div className="flex gap-2">
            <Link
              to={`/people/${personId}/edit`}
              className="rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-navy hover:bg-bg"
            >
              Edit
            </Link>
            {isAdmin && (
              <button
                type="button"
                onClick={() => { del.reset(); setConfirmOpen(true); }}
                className="rounded border border-red/40 bg-white px-3 py-1.5 text-sm font-medium text-red hover:bg-red/5"
              >
                Delete
              </button>
            )}
          </div>
        )}
      </div>

      <div className="mb-4 flex gap-1 border-b border-border text-sm">
        {([
          ['profile', 'Profile'],
          ['devices', `Assets (${counts.DevicesAssigned})`],
          ['held', `Held (${counts.Held})`],
          ['software', `Software (${counts.RoleSoftware})`],
          ['sims', `SIMs (${counts.Sims})`],
          // OnsiteLive reads are admin + helpdesk (OnsiteLiveAdminFunctions).
          ...(canMutate ? [['onsitelive', 'OnsiteLive'] as [Tab, string]] : []),
          ['leaving', 'Leaving'],
          ['audit', 'Audit'],
        ] as [Tab, string][]).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={
              tab === key
                ? 'border-b-2 border-orange px-3 py-2 font-semibold text-navy'
                : 'px-3 py-2 text-darkGrey/70 hover:text-navy'
            }
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'profile' && (
        <>
        <dl className="grid grid-cols-1 gap-y-2 text-sm sm:grid-cols-2">
          <Row label="Employee ID" value={p.EmployeeId} />
          <Row label="Preferred name" value={p.PreferredName} />
          <Row label="Primary email" value={p.PrimaryEmail} />
          <Row label="Username" value={p.Username} />
          <Row label="Mobile" value={p.Mobile} />
          <Row label="Work phone" value={p.Phone} />
          <Row label="Location" value={p.LocationName ?? (p.LocationId != null ? `#${p.LocationId}` : null)} />
          <Row label="Primary branch / cost centre" value={p.HomeBranchCode ? `${p.HomeBranchCode}${p.HomeBranchName ? ` ${p.HomeBranchName}` : ''}` : null} />
          <EntraOfficeRow p={p} />
          <CostCentreSplit personId={p.PersonId} canEdit={canMutate} />
          <Row label="Department" value={departmentLabel(p)} />
          <div className="flex gap-2">
            <dt className="w-40 shrink-0 font-semibold text-darkGrey">Manager</dt>
            <dd className="text-darkGrey/80">
              {p.ManagerPersonId ? (
                <Link to={`/people/${p.ManagerPersonId}`} className="text-navy hover:underline">
                  {p.ManagerDisplayName ?? `#${p.ManagerPersonId}`}
                </Link>
              ) : '—'}
            </dd>
          </div>
          <Row label="Employment start" value={formatDate(p.EmploymentStartDate)} />
          <Row label="Status" value={p.AccountStatus} />
          <Row label="Entra account" value={p.Active ? 'Enabled' : 'Disabled'} />
          <Row label="Last sign-in" value={p.LastSignInDateTime ? formatDateTime(p.LastSignInDateTime) : null} />
          <Row label="Last password change" value={p.LastPasswordChangeDateTime ? formatDateTime(p.LastPasswordChangeDateTime) : null} />
          <Row label="Notes" value={p.Notes} />
        </dl>
        </>
      )}

      {tab === 'devices' && <PersonAssets personId={personId} sims={false} />}
      {tab === 'held' && <PersonAssets personId={personId} sims={false} held />}
      {tab === 'software' && (
        <PersonRoleSoftware
          personId={personId}
          isAdmin={isAdmin}
          departmentCode={p.DepartmentCode}
          departmentLabel={departmentLabel(p)}
        />
      )}
      {tab === 'sims' && <PersonAssets personId={personId} sims />}
      {tab === 'onsitelive' && canMutate && <PersonOnsiteLive personId={personId} />}
      {tab === 'leaving' && <PersonLeaving person={p} />}
      {tab === 'audit' && <AuditTab personId={personId} />}

      <AlertDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete this person?"
        description={`${p.DisplayName} will be soft-deleted and hidden from the register. This is reversible by an administrator.`}
        confirmLabel="Delete"
        destructive
        closeOnConfirm={false}
        onConfirm={() => del.mutate()}
      >
        {del.isError && (
          <p role="alert" className="text-xs text-red">
            {del.error instanceof ApiError ? del.error.message : 'Could not delete this person.'}
          </p>
        )}
      </AlertDialog>
    </section>
  );
}

/**
 * The office and OU Entra holds (BTG assigns them in AD until Oscar provisions them).
 * Flagged when Entra's office is not the AD office of the person's Oscar location.
 */
function EntraOfficeRow({ p }: { p: Person }) {
  if (!p.EntraOfficeLocation && !p.EntraOu) return null;
  const differs = p.EntraOfficeLocation != null && p.EntraLocationId !== p.LocationId;
  return (
    <div className="flex gap-2">
      <dt className="w-40 shrink-0 font-semibold text-darkGrey">Entra office</dt>
      <dd className="min-w-0 text-darkGrey/80">
        {p.EntraOfficeLocation ?? '—'}
        {differs && (
          <span className="ml-2 rounded border border-orange px-1.5 py-0.5 text-xs text-darkGrey" title="Entra's office is not the AD office of this person's Oscar location">
            {p.EntraLocationName ? `Entra matches ${p.EntraLocationName}` : 'No Oscar location has this AD office'}
          </span>
        )}
        {p.EntraOu && <span className="block break-all text-xs text-darkGrey/70" title="OU (from Entra)">{p.EntraOu}</span>}
      </dd>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex gap-2">
      <dt className="w-40 shrink-0 font-semibold text-darkGrey">{label}</dt>
      <dd className="text-darkGrey/80">{value && value !== '—' ? value : '—'}</dd>
    </div>
  );
}

/** Assets currently assigned to the person. `sims` toggles between physical
 *  devices (the Assets tab) and SIM assets (the SIMs tab). `held` switches to the
 *  assets sited at a location that this person is RESPONSIBLE for (mig 060) —
 *  not assigned to them, but the list a handover has to work through. */
function PersonAssets({ personId, sims, held = false }: { personId: number; sims: boolean; held?: boolean }) {
  const query = useQuery({
    queryKey: ['person-assets', personId, sims, held],
    queryFn: ({ signal }) => listAssets(
      held ? { heldByPersonId: personId, pageSize: 200 } : { personId, category: sims ? 'SIM' : undefined, pageSize: 200 },
      signal,
    ),
  });
  // Held assets sit at a location, so name it rather than showing a bare id.
  const locationsQuery = useQuery({ queryKey: ['locations', 'active'], queryFn: ({ signal }) => getLocations({ activeOnly: true }, signal), enabled: held, retry: false });
  const locationName = new Map((locationsQuery.data ?? []).map((l) => [l.LocationId, l.LocationDesc] as const));

  if (query.isLoading) return <p className="text-sm text-darkGrey/70">Loading…</p>;
  if (query.isError) return <p className="text-sm text-red">Could not load assigned items.</p>;

  // The Assets tab excludes SIMs (the SIMs tab shows those), matching the counts.
  // The Held tab has no category split — everything the person is responsible for.
  const items = held ? (query.data?.Items ?? []) : (query.data?.Items ?? []).filter((a) => (sims ? a.Category === 'SIM' : a.Category !== 'SIM'));
  if (items.length === 0) {
    return (
      <p className="text-sm text-darkGrey/70">
        {held ? 'Nothing held. Assets sited at a location can name this person as responsible.' : `No ${sims ? 'SIMs' : 'assets'} assigned.`}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded border border-border bg-white shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
          <tr>
            <th className="px-3 py-2">{sims ? 'Number / ICCID' : 'Serial / tag'}</th>
            {!sims && <th className="px-3 py-2">Hostname</th>}
            {!sims && <th className="px-3 py-2">Number</th>}
            <th className="px-3 py-2">Category</th>
            <th className="px-3 py-2">Make / model</th>
            {held && <th className="px-3 py-2">Location</th>}
            <th className="px-3 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.AssetId} className="border-t border-border hover:bg-bg/60">
              <td className="px-3 py-2 font-medium text-navy">
                <Link to={`/assets/${a.AssetId}`} className="hover:underline">
                  {a.Msisdn ?? a.SerialNumber ?? a.AssetTag ?? `#${a.AssetId}`}
                </Link>
              </td>
              {!sims && <td className="px-3 py-2 text-darkGrey/80">{a.Hostname ?? '—'}</td>}
              {!sims && <td className="px-3 py-2 text-darkGrey/80">{a.Msisdn ?? a.IntunePhoneNumber ?? '—'}</td>}
              <td className="px-3 py-2 text-darkGrey/80">{a.Category}</td>
              <td className="px-3 py-2 text-darkGrey/80">{[a.Make, a.Model].filter(Boolean).join(' ') || '—'}</td>
              {held && <td className="px-3 py-2 text-darkGrey/80">{a.LocationId != null ? (locationName.get(a.LocationId) ?? `#${a.LocationId}`) : '—'}</td>}
              <td className="px-3 py-2 text-darkGrey/80">{a.LifecycleState}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Resolves the display label for a person's department: the derived code +
 *  description when linked, else the legacy free-text value. */
function departmentLabel(p: {
  DepartmentCode: string | null;
  DepartmentDescription: string | null;
  Department: string | null;
}): string | null {
  if (p.DepartmentCode) {
    return p.DepartmentDescription ? `${p.DepartmentCode} — ${p.DepartmentDescription}` : p.DepartmentCode;
  }
  return p.Department;
}

/** Software this person's role is entitled to, drawn from the department's
 *  Onboarding Software Allocation (oscar.DepartmentSoftwarePool). Split into
 *  Mandatory (auto-provisioned on hire) and Optional (available on request).
 *  Admins can force provisioning now rather than waiting for the hourly sweep. */
function PersonRoleSoftware({
  personId,
  isAdmin,
  departmentCode,
  departmentLabel,
}: {
  personId: number;
  isAdmin: boolean;
  departmentCode: string | null;
  departmentLabel: string | null;
}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['departmentSoftware', departmentCode],
    queryFn: ({ signal }) => listDepartmentSoftware(departmentCode!, signal),
    enabled: !!departmentCode,
  });

  const provision = useMutation({
    mutationFn: () => provisionPersonSoftware(personId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['person', personId] }),
  });

  if (!departmentCode) {
    return (
      <p className="text-sm text-darkGrey/70">
        No department set. Choose a job title on the person's profile to see the software entitled to their role.
      </p>
    );
  }
  if (query.isLoading) return <p className="text-sm text-darkGrey/70">Loading…</p>;
  if (query.isError) return <p className="text-sm text-red">Could not load role software.</p>;

  const items = query.data ?? [];
  const mandatory = items.filter((i) => i.IsMandatory);
  const optional = items.filter((i) => !i.IsMandatory);

  if (items.length === 0) {
    return (
      <p className="text-sm text-darkGrey/70">
        No software is allocated to {departmentLabel ?? departmentCode}. Allocations are managed under Admin → Onboarding → Software Allocation.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-darkGrey/70">
          Software entitled to the <span className="font-semibold text-navy">{departmentLabel ?? departmentCode}</span> role.
          {mandatory.length > 0 && ' Mandatory items are auto-provisioned on hire and reconciled hourly.'}
        </p>
        {isAdmin && mandatory.length > 0 && (
          <button
            type="button"
            onClick={() => provision.mutate()}
            disabled={provision.isPending}
            className="shrink-0 rounded border border-navy/30 bg-white px-3 py-1.5 text-sm font-medium text-navy hover:bg-bg disabled:opacity-50"
          >
            {provision.isPending ? 'Provisioning…' : 'Provision now'}
          </button>
        )}
      </div>

      {provision.isError && (
        <div role="alert" className="rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">
          {provision.error instanceof ApiError ? provision.error.message : 'Could not provision role software.'}
        </div>
      )}
      {provision.isSuccess && (
        <div role="status" className="rounded border border-green/30 bg-green/10 px-3 py-2 text-sm text-green">
          {provision.data.Created > 0
            ? `Provisioned ${provision.data.Created} licence${provision.data.Created === 1 ? '' : 's'}.`
            : 'Already up to date — nothing to provision.'}
          {provision.data.Pools.some((pool) => pool.OverAllocated) && ' Some pools are now over-allocated — check seat counts.'}
        </div>
      )}

      <SoftwareGroup title="Mandatory" hint="Auto-provisioned on hire" items={mandatory} />
      <SoftwareGroup title="Optional" hint="Available on request" items={optional} />
    </div>
  );
}

function SoftwareGroup({ title, hint, items }: { title: string; hint: string; items: DepartmentSoftware[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
        {title} <span className="font-normal normal-case text-darkGrey/50">· {hint}</span>
      </h3>
      <ul className="overflow-hidden rounded border border-border bg-white shadow-sm">
        {items.map((a) => (
          <li key={a.DepartmentSoftwarePoolId} className="flex items-center gap-2 border-t border-border px-3 py-2 text-sm first:border-t-0">
            <span className="flex-1 text-darkGrey/90">
              {a.Title}
              {a.SKU && <span className="ml-2 font-mono text-xs text-darkGrey/50" title="SKU (text)">{a.SKU}</span>}
              {a.SkuId != null && <span className="ml-2 font-mono text-xs text-darkGrey/50" title="SKU (ID)">#{a.SkuId}</span>}
            </span>
            <span className="text-xs text-darkGrey/50">{a.LicenceType}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AuditTab({ personId }: { personId: number }) {
  const query = useQuery({
    queryKey: ['audit', 'Person', personId],
    queryFn: ({ signal }) => getAudit({ entityType: 'Person', entityId: personId }, signal),
  });

  if (query.isLoading) return <p className="text-sm text-darkGrey/70">Loading…</p>;
  if (query.isError) return <p className="text-sm text-red">Could not load audit history.</p>;
  if (!query.data || query.data.length === 0) return <p className="text-sm text-darkGrey/70">No audit history.</p>;

  return (
    <ul className="space-y-2 text-sm">
      {query.data.map((a) => (
        <li key={a.AuditId} className="rounded border border-border bg-white px-3 py-2">
          <span className="font-semibold text-navy">{a.Action}</span>
          <span className="text-darkGrey/70"> · {formatDateTime(a.ChangedDate)} · {a.ChangedBy}</span>
        </li>
      ))}
    </ul>
  );
}
